import {createAsyncThunk, createSlice, nanoid, PayloadAction} from "@reduxjs/toolkit";
import {
    FieldType,
    Filter,
    FilterInputI,
    FilterValueI,
    Primitive,
    PrimitiveObject,
    QueryOrder,
    QueryParamsDTO,
    SqlOperation,
    Strings,
    TableFieldSetSettingsI
} from "../Types";
import {schemas} from "../config";
import {RootState} from "../store/store";
import {datetimeDefValue} from "../components/filters/DatetimeFilterValue";
import {createMapFilterSchema} from "./init";
import {toast} from "react-toastify";
import {LANG} from "../components/CONSTANTS";
import {apiFetch, ApiResult} from "../components/util";


export type queryU = "prev" | "current";
export type Query = {
    [K in queryU]: queryI;
};


export interface tableInfoI {
    current_page: number,
    prev_page: number,
    max_pages: number,
    selectedFieldSet: string
    forAdmin: boolean,
    hidden: boolean,
    name: Strings
    loading: "idle" | "loading" | "error",
    frozen: { [key: string]: boolean | undefined },
    openRows: { [key: string]: boolean | undefined },
    asyncMaps: {
        loading: "idle" | "loading" | "loaded" | "error",
        data: {
            key: string
            queryKey: string
        } []

    }
}


export interface queryI {
    limit: number,
    orderBy: string,
    orderWay: "ASC" | "DESC",
    filters: Partial<FilterInputI>[]
}

export interface countI {
    total: number | undefined;
    inCondition: number | undefined;
}

export type RefDataOI = {
    data: PrimitiveObject[],
    status: "idle" | "loading" | "loaded" | "error"
    open: boolean
    dt: number,
    inCondition: number
};

export interface TableSliceInitialStateI {
    pages: { [p: string]: { [p: string]: PrimitiveObject[] } };
    refEntities: {
        [name: string]: {
            [key: string]: RefDataOI
        }
    };
    queries: { [p: string]: Query };
    activeTable: string;
    loadTableCountsStatus: "idle" | "loading" | "loaded" | "error",
    tableInfo: { [p: string]: tableInfoI };
    names: { [p: string]: { [p: string]: string } };
    count: { [p: string]: countI };
}

type SelectFilterFieldActionPayloadI = {
    index: number,
    field: string,
    fieldType: FieldType
};
export const tablesSlice = createSlice({
    name: 'table',
    initialState: {} as TableSliceInitialStateI,
    reducers: {
        setInitialTableState(state, action) {
            return {
                ...action.payload
            }
        },
        setActiveTable(state, action) {
            state.activeTable = action.payload
        },
        moveActiveTable(state, action: PayloadAction<number>) {
            let move = action.payload
            const names = Object.entries(state.tableInfo).filter(([key, t]) => !t.hidden).map(([key, _]) => key);
            const active = names.findIndex(x => x === state.activeTable);
            state.activeTable = names[Math.max(0, Math.min(active + move, names.length - 1))]
        },
        setTableHidden(state, action: PayloadAction<{ table: string, val: boolean }>) {
            const {table, val} = action.payload
            state.tableInfo[table].hidden = val
        },
        setSelectedTableFieldSet(state, action: PayloadAction<{ table: string, set: string }>) {
            const {table, set} = action.payload
            state.tableInfo[table].selectedFieldSet = set
            state.pages[table] = {}
        },
        setTableRefOpen(state, action: PayloadAction<{ entity: string, refKey: string, open: boolean }>) {
            const {entity, refKey, open} = action.payload
            if (state.refEntities[entity][refKey]) {
                state.refEntities[entity][refKey].open = open
            }
        },
        updateCurrentRecord(state, action: PayloadAction<{ pkKey: string, record: PrimitiveObject }>) {
            const currentPage = state.tableInfo[state.activeTable].current_page;
            const {pkKey, record} = action.payload;
            let find = state.pages[state.activeTable][currentPage].findIndex(r => r[pkKey] === record[pkKey]);
            if (find === -1)
                console.debug('item with search pkField ', pkKey, record[pkKey], ' was not found')
            else {
                console.debug('item with search pkField ', pkKey, record[pkKey], ' WAS found')
                state.pages[state.activeTable][currentPage][find] = {
                    ...record,
                    n: state.pages[state.activeTable][currentPage][find]['n']
                }
            }
        },
        changePage(state, action: PayloadAction<number>) {
            state.tableInfo[state.activeTable].prev_page = state.tableInfo[state.activeTable].current_page
            state.tableInfo[state.activeTable].current_page = action.payload
        },
        changePerPage(state, action: PayloadAction<number>) {
            state.queries[state.activeTable].current.limit = action.payload
        },
        setTableRowOpen(state, action: PayloadAction<{ pk: string, table: string, open: boolean }>) {
            const {pk, table, open} = action.payload
            state.tableInfo[table].openRows[pk] = open
        },
        switchToTablePK(state, action: PayloadAction<{ table: string, project: string, record: PrimitiveObject }>) {
            const {table, record, project} = action.payload;
            const tableSchema = schemas[project].tables[table];
            const pkField = tableSchema.fields.find(x => x.key === tableSchema.pk);
            if (!pkField)
                return
            if (!record[tableSchema.pk]) {
                toast.warn("Запис не містить поля з основним ключем")
                return;
            }
            state.queries[table].current.filters = [{
                field: pkField.key,
                filterOp: SqlOperation.EQUAL,
                value: record[pkField.key],
                locked: true,
                type: pkField.type,
                nanoid: nanoid()
            }]
            state.activeTable = table

        },
        setTableFilters(state, action: PayloadAction<Partial<FilterInputI>[]>) {
            state.queries[state.activeTable].current.filters = action.payload
        },
        addNewFilter(state, action: PayloadAction<string>) {
            state.queries[action.payload].current.filters.push({
                nanoid: nanoid()
            })
        },
        mergeTableFilters(state, action: PayloadAction<{ table: string, filters: FilterInputI[] }>) {
            const {table, filters} = action.payload;
            let temp = state.queries[table].current.filters

            filters.forEach(f => {
                const index = temp.findIndex(x => x.field === f.field);
                if (index !== -1) {
                    const currFilter = temp[index];
                    if ((f.filterOp === SqlOperation.IN && currFilter.filterOp === SqlOperation.IN) || (f.filterOp === SqlOperation.NOT_IN && currFilter.filterOp === SqlOperation.NOT_IN)) {
                        const set = new Set(((currFilter.value as any[]) || []).concat(f.value));
                        temp[index] = {...currFilter, value: Array.from(set), nanoid: nanoid()}
                    } else
                        temp[index] = f
                } else temp.push(f)
            })
        },
        deleteAllFilters(state, action: PayloadAction<string>) {
            state.queries[action.payload]!.current.filters = []
        },
        deleteFilter(state, action: PayloadAction<number>) {
            state.queries[state.activeTable].current.filters.splice(action.payload, 1)
        },
        selectFilterField(state, action: PayloadAction<SelectFilterFieldActionPayloadI>) {
            const {index, field, fieldType} = action.payload;
            state.queries[state.activeTable].current.filters[index].field = field
            state.queries[state.activeTable].current.filters[index].type = fieldType

        },
        selectFilterOperation(state, action: PayloadAction<{ index: number, op: SqlOperation, type: FieldType }>) {
            const {index, op, type} = action.payload;
            let filter = state.queries[state.activeTable].current.filters[index];
            filter.filterOp = op
            filter.locked = true
            let defVal = defaultFilterValue(type);
            if (op === SqlOperation.BETWEEN) {
                filter.value = [defVal, defVal] as any
            } else if (op === SqlOperation.IN || op === SqlOperation.NOT_IN) {
                filter.value = []
            } else if (op === SqlOperation.IS_NOT_NULL || op === SqlOperation.IS_NULL) {
                filter.value = true
            } else {
                filter.value = defVal
            }
        },
        setFilterValue(state, action: PayloadAction<{ index: number, value: FilterValueI, arrPos?: number }>) {
            const {index, value, arrPos} = action.payload;
            let filter = state.queries[state.activeTable].current.filters[index];
            if (arrPos !== undefined) {
                (filter.value as any)[arrPos] = value as string | number
            } else
                filter.value = value
        },
        setOrderBy(state, action: PayloadAction<string>) {
            state.queries[state.activeTable].current.orderBy = action.payload
        },
        changeOrderWay(state, action: PayloadAction<undefined>) {
            state.queries[state.activeTable].current.orderWay = state.queries[state.activeTable].current.orderWay === 'ASC' ? 'DESC' : 'ASC'
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTableByQuery.pending, (state, action) => {
                state.tableInfo[state.activeTable].loading = "loading";
            })
            .addCase(fetchTableByQuery.fulfilled, (state, action) => {
                const payload = action.payload;
                const query = payload.query;
                const tableName = query.table;
                const pageIndex = query.pageIndex.toString();
                if (payload.result.status === 'error') {
                    state.tableInfo[tableName].loading = "error";
                } else {
                    if (query.clean) {
                        state.pages[tableName] = {}
                        state.tableInfo[tableName].openRows = {}
                    }
                    const data = payload.result.data as PrimitiveObject[];
                    if (data.length === 1) {
                        const pk = schemas[query.project].tables[tableName].pk;
                        if (data[0][pk])
                            state.tableInfo[tableName].openRows = {[String(data[0][pk])]: true}
                    }
                    state.pages[tableName][pageIndex] = data;
                    state.count[tableName].inCondition = payload.result.count;
                    state.tableInfo[tableName].max_pages = Math.ceil(payload.result.count / query.pages);
                    state.tableInfo[tableName].current_page = query.pageIndex;
                    state.tableInfo[tableName].loading = "idle";
                }
            })
            .addCase(fetchTableByQuery.rejected, (state, action: any) => {
                state.tableInfo[state.activeTable].loading = "error";
                toast.error(action.payload?.result?.message || "Сталась помилка при завантаженні таблиці")
            })
            .addCase(fetchTableRefEntity.pending, (state, action) => {
                const payload = action.meta.arg;                 // ← access initial payload here
                const {refEntityName, refKey} = payload;
                state.refEntities[refEntityName][refKey] = {
                    data: [],
                    open: true,
                    dt: Date.now(),
                    status: "loading",
                    inCondition: 0
                };
            })
            .addCase(fetchTableRefEntity.fulfilled, (state, action) => {
                const payload = action.payload;
                const query = payload.query;
                if (payload.result.status === 'error') {
                    state.refEntities[query.refEntityName][query.refKey] = {
                        data: [],
                        open: true,
                        dt: new Date().getTime(),
                        status: "error",
                        inCondition: 0
                    }
                } else {
                    state.refEntities[query.refEntityName][query.refKey] = {
                        data: payload.result.data as PrimitiveObject[],
                        dt: new Date().getTime(),
                        status: "loaded",
                        open: true,
                        inCondition: payload.result.count || 0
                    }
                }
            })
            .addCase(fetchTableRefEntity.rejected, (state, action: any) => {
                const payload = action.payload;
                const query = payload.query;
                toast.error(action.payload?.result?.message || "Сталась помилка при завантаженні таблиці")
                state.refEntities[query.refEntityName][query.refKey] = {
                    data: [],
                    dt: new Date().getTime(),
                    status: "error",
                    open: true,
                    inCondition: 0
                }
            });

        builder.addCase(fetchTableCounts.pending, (state, action) => {
            state.loadTableCountsStatus = "loading"
        })
            .addCase(fetchTableCounts.fulfilled, (state, action) => {
                action.payload.result.count.map((x: { 'table_name': string; count: number; }) => {
                    if (state.count[x['table_name']])
                        state.count[x['table_name']].total = x.count
                    return null;
                })
                state.loadTableCountsStatus = "loaded"
            })
            .addCase(fetchTableCounts.rejected, (state, action) => {
                state.loadTableCountsStatus = "error"
            })

        builder
            .addCase(updateSingleTableField.pending, (state, action) => {
                let activeTable = state.activeTable;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = true
            })
            .addCase(updateSingleTableField.rejected, (state, action) => {
                let activeTable = state.activeTable;
                let {result, payload, table, page, pkField} = action.payload as UpdateSingleFieldRejectedPayload;
                ;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = undefined

                let find = state.pages[table][page].find(r => r[pkField] === payload.id);
                if (result.extra_fields !== undefined && find !== undefined) {
                    result.extra_fields.forEach((f: {
                        field: string;
                        value: string | number | boolean | null;
                    }) => find![f.field] = f.value)
                }
                // @ts-ignore
                toast.error(action.payload?.result?.message)
            })
            .addCase(updateSingleTableField.fulfilled, (state, action) => {
                let {result, payload, table, page, pkField} = action.payload;
                state.tableInfo[table].frozen[table + payload.id] = undefined
                if (result.status === 'success') {
                    let find = state.pages[table][page].find(r => r[pkField] === payload.id);
                    if (!find)
                        console.log('item with search pkField ', table + page + payload.id, ' was not found')
                    else {
                        console.log('item with search pkField ', table + page + payload.id, ' WAS found')
                        find[payload.field] = payload.value

                    }
                    toast.success(action.payload?.result?.message)

                    if (result.extra_fields !== undefined && find !== undefined) {
                        result.extra_fields.forEach((f: {
                            field: string;
                            value: string | number | boolean | null;
                        }) => find![f.field] = f.value)
                    }
                }

            })

        builder
            .addCase(nullifyTableField.pending, (state, action) => {
                let activeTable = state.activeTable;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = true
            })
            .addCase(nullifyTableField.rejected, (state, action) => {
                let activeTable = state.activeTable;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = undefined
                // @ts-ignore
                toast.error(action.payload?.result?.message)
            })
            .addCase(nullifyTableField.fulfilled, (state, action) => {
                let {result, payload, table, page, pkField} = action.payload;
                state.tableInfo[table].frozen[table + payload.id] = undefined
                if (result.status === 'success') {
                    let find = state.pages[table][page].find(r => r[pkField] === payload.id);
                    if (!find)
                        console.log('item with search pkField ', table + page + payload.id, ' was not found')
                    else {
                        console.log('item with search pkField ', table + page + payload.id, ' WAS found')
                        find[payload.field] = null
                    }
                    toast.success(action.payload?.result?.message)
                }
            })

        builder
            .addCase(deleteTableRecord.pending, (state, action) => {
                let activeTable = state.activeTable;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = true
            })
            .addCase(deleteTableRecord.rejected, (state, action) => {
                let activeTable = state.activeTable;
                state.tableInfo[activeTable].frozen[activeTable + action.meta.arg.id] = undefined
            })
            .addCase(deleteTableRecord.fulfilled, (state, action) => {
                let {payload, table, page, pkField} = action.payload;
                state.tableInfo[table].frozen[table + payload.id] = undefined
                state.pages[table][page] = state.pages[table][page].filter(r => r[pkField] !== payload.id);
                toast.success(action.payload?.result?.message)
            })


        builder
            .addCase(fetchAsyncMap.pending, (state, action) => {
                state.tableInfo[action.meta.arg.table].asyncMaps.loading = 'loading'
            })
            .addCase(fetchAsyncMap.rejected, (state, action) => {
                state.tableInfo[action.meta.arg.table].asyncMaps.loading = 'error'
            })
            .addCase(fetchAsyncMap.fulfilled, (state, action) => {
                let {project, table} = action.meta.arg;
                action.payload.result.forEach(r => {
                    let field = schemas[project].tables[table].fields.find(x => x.key === r.key);
                    if (field) {
                        field.map = {
                            async: field.map?.async,
                            values: r!.data
                        }
                        schemas[project].filterSchemas[table][r.key] = {
                            ...schemas[project].filterSchemas[table][r.key],
                            mapSchema: createMapFilterSchema(field.map)
                        }
                    }
                })
                state.tableInfo[table].asyncMaps.loading = 'loaded'
            })
    },
})

type ExtendedQueryDto = QueryParamsDTO & { clean?: true };
export const fetchTableByQuery = createAsyncThunk<{
    query: ExtendedQueryDto,
    result: any
}, Partial<ExtendedQueryDto> | undefined, {
    state: RootState
}>(
    'table/fetchTableByQuery',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();
        let tableName = state.tables.activeTable;

        const set = schemas[state.project.name].tables[tableName].fieldSets;

        const tInfo = state.tables.tableInfo[tableName];
        const currentQuery = state.tables.queries[tableName].current;
        let query = {
            project: state.project.name,
            table: tableName,
            fields: (set && tInfo.selectedFieldSet) ? getFieldsForTableSet(set, tInfo.selectedFieldSet) : [],
            pages: payload?.pages || currentQuery.limit,
            pageIndex: payload?.pageIndex || tInfo.current_page,
            filters: []
        } as ExtendedQueryDto
        query.filters = currentQuery.filters.map(f => {
            if ((f.filterOp === SqlOperation.LIKE || f.filterOp === SqlOperation.NOT_LIKE) && f.value === '')
                return null
            if ((f.filterOp === SqlOperation.IN || f.filterOp === SqlOperation.NOT_IN) && ((f.value as [])?.length === 0 || f.value === undefined))
                return null

            return f.field && f.filterOp && f.value !== undefined ? {
                field: f.field,
                filterOp: f.filterOp,
                value: f.value
            } as Filter : null
        }).filter(x => x !== null) as Filter[]
        query.order = {
            field: currentQuery.orderBy,
            way: currentQuery.orderWay
        }
        query.clean = payload?.clean
        let json = {...query}
        delete json.clean
        let jsonStr = JSON.stringify(json);
        let uri = schemas[query.project].backend + '/achillea/selectTableData.php?query=' + encodeURIComponent(jsonStr);
        const response = await fetch(uri, {credentials: 'include'})
            .then(r => {
                if (r.status === 401) {
                    window.location.href = '/login.html'
                }
                return r
            })
            .then(r => r.json());

        if (response.status === 'error')
            return thunkAPI.rejectWithValue({
                query: query,
                result: response
            })

        return {
            query: query,
            result: response
        }
    },
)

function getFieldsForTableSet(set: TableFieldSetSettingsI, selected: string): string[] {
    if (!set.sets[selected])
        return []
    return set.sets[selected].fields.map(f => f.key)
}

type XlsQueryDto = QueryParamsDTO & { fields: { [key: string]: string }[] };
export const fetchXls = createAsyncThunk<{
    query: ExtendedQueryDto,
    result: any
}, Partial<XlsQueryDto> | undefined, {
    state: RootState
}>(
    'table/fetchXls',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();
        let tableName = state.tables.activeTable;
        const lang = LANG
        let query = {
            project: state.project.name,
            table: tableName,
            fields: [],
            pages: 0,
            pageIndex: payload?.pageIndex || state.tables.tableInfo[tableName].current_page,
            filters: []
        } as ExtendedQueryDto
        query.filters = state.tables.queries[tableName].current.filters.map(f => {
            if (f.filterOp === SqlOperation.LIKE && f.value === '')
                return null
            if ((f.filterOp === SqlOperation.IN || f.filterOp === SqlOperation.NOT_IN) && ((f.value as [])?.length === 0 || f.value === undefined))
                return null

            return f.field && f.filterOp && f.value !== undefined ? {
                field: f.field,
                filterOp: f.filterOp,
                value: f.value
            } as Filter : null
        }).filter(x => x !== null) as Filter[]
        query.order = {
            field: state.tables.queries[tableName].current.orderBy,
            way: state.tables.queries[tableName].current.orderWay
        }
        let json = {...query}
        let jsonStr = JSON.stringify(json);
        let fields = schemas[query.project].tables[tableName].fields
        let maps = {} as any;
        schemas[query.project].tables[tableName].fields.filter(f => f.map && !f.map.async).forEach(f => {
            let keys = {} as any
            Object.entries(f.map!.values).forEach(([key, v]) => {
                keys[v.rawValue.toString()] = v.lang[lang]
            })
            maps[f.key] = keys
        })
        let asyncMaps = schemas[query.project].tables[tableName].fields
            .filter(f => f.map && f.map.async).map(f => {
                return {key: f.key, query: f.map!.async}
            })
        let fieldLang = JSON.stringify(fields.map(f => ({
            value: f.title[lang],
            key: f.key
        })));
        let uri = schemas[query.project].backend + '/achillea/getXls.php?query=' + encodeURIComponent(jsonStr)
            + '&langField=' + encodeURIComponent(fieldLang) + '&maps=' + encodeURIComponent(JSON.stringify(maps))
            + '&asyncMaps=' + encodeURIComponent(JSON.stringify(asyncMaps));
        window.open(uri, "_blank")
        return {
            query: query,
            result: {}
        }
    },
)

export const fetchTableRefEntity = createAsyncThunk<{
    query: QueryParamsDTO & { refEntityName: string, refKey: string },
    result: any
}, {
    tableName: string,
    pageIndex: number,
    limit: number,
    fields: string[],
    filters: Filter[],
    order: QueryOrder,
    refEntityName: string,
    refKey: string
}, {
    state: RootState
}>(
    'table/fetchTableRefEntity',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();
        let query = {
            project: state.project.name,
            table: payload.tableName,
            fields: payload.fields,
            pages: payload.limit,
            pageIndex: 1,
            filters: payload.filters,
            order: payload.order
        } as QueryParamsDTO

        let jsonStr = JSON.stringify(query);
        let uri = schemas[query.project].backend + '/achillea/selectTableData.php?query=' + encodeURIComponent(jsonStr);
        const response = await fetch(uri, {credentials: 'include'})
            .then(r => {
                if (r.status === 401) {
                    window.location.href = '/login.html'
                }
                return r
            })
            .then(r => r.json());
        query = {...query, refEntityName: payload.refEntityName, refKey: payload.refKey} as QueryParamsDTO & {
            refEntityName: string,
            refKey: string
        }
        if (response.status === 'error')
            return thunkAPI.rejectWithValue({
                query: query as any,
                result: response
            })
        return {
            query: query as any,
            result: response
        }
    },
)


export const fetchTableCounts = createAsyncThunk<any, string>(
    'table/fetchTableCounts',
    async (payload, thunkAPI) => ({
        project: payload,
        result: await fetch(schemas[payload].backend + '/achillea/selectTablesCount.php?project=' + payload, {
            credentials: 'include'
        })
            .then(r => {
                if (r.status === 401) {
                    window.location.href = '/login.html'
                }
                return r
            })
            .then(r => r.json())
    }),
)


export const fetchAsyncMap = createAsyncThunk<{
    payload: { project: string; data: { key: string; queryKey: string }[]; table: string };
    result: { data: any; key: string }[];
}, {
    project: string,
    data: {
        key: string,
        queryKey: string
    }[],
    table: string
}, {
    state: RootState
}>(
    'table/fetchAsyncMap',
    async (payload, thunkAPI) => {
        try {
            const data = {} as any
            const maps = await Promise.all(
                payload.data.map(async x => {
                    const res = await fetch(
                        `${schemas[payload.project].backend}/achillea/runSqlQuery.php?project=${payload.project}&query_key=${x.queryKey}`,
                        {credentials: 'include'})
                        .then(r => {
                            if (r.status === 401) {
                                window.location.href = '/login.html'
                            }
                            return r
                        })
                        .then(r => r.json()) as {
                        data: { label: string; value: string | number }[]
                    };

                    res.data.forEach(y => {
                        data[y.value] = {
                            rawValue: y.value,
                            lang: {ua: y.label},
                        }
                    })
                    return {
                        key: x.key,
                        data: data
                    };
                })
            );


            return {
                payload: payload,
                result: maps
            };
        } catch (e) {
            console.log(e)
            // @ts-ignore
            return thunkAPI.rejectWithValue({message: e.message});
        }

    },
)

export const updateSingleTableField = createAsyncThunk<{
    payload: {
        field: string,
        value: Primitive,
        id: number | string
    },
    table: string,
    page: number,
    pkField: string,
    result: any
}, {
    field: string,
    value: Primitive,
    id: number | string
}, {
    state: RootState
}>(
    'table/updateSingleField',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();

        let tableName = state.tables.activeTable;
        let project = state.project.name;

        let path
        const schema = schemas[project];
        let field = schema.tables[tableName].fields.find(x => x.key === payload.field);
        if (field?.updateSingleFieldPath)
            path = schema.backend + schema.tables[tableName].backend + field.updateSingleFieldPath;
        else
            path = schema.backend + '/achillea/updateTableField.php';
        let result = await fetch(path, {
            method: "PATCH",
            credentials: 'include',
            body: JSON.stringify({
                project: project,
                table: tableName,
                pk: payload.id,
                field: payload.field,
                value: payload.value
            })
        })
            .then(r => {
                if (r.status === 401) {
                    window.location.href = '/login.html'
                }
                return r
            })
            .then(r => r.json());

        if (result.status === 'error')
            return thunkAPI.rejectWithValue({
                payload: payload,
                table: tableName,
                page: state.tables.tableInfo[tableName].current_page,
                pkField: schema.tables[tableName].pk,
                result: result
            })

        return {
            payload: payload,
            table: tableName,
            page: state.tables.tableInfo[tableName].current_page,
            pkField: schema.tables[tableName].pk,
            result: result
        }
    },
)

type UpdateSingleFieldRejectedPayload = {
    payload: {
        field: string;
        value: Primitive;
        id: number | string;
    };
    table: string;
    page: number;
    pkField: string;
    result: any;
};

export const nullifyTableField = createAsyncThunk<{
    payload: {
        field: string,
        id: number | string
    },
    table: string,
    page: number,
    pkField: string,
    result: any
}, {
    field: string,
    id: number | string
}, {
    state: RootState
}>(
    'table/nullifyTableField',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();

        let tableName = state.tables.activeTable;
        let project = state.project.name;

        const schema = schemas[project];
        let field = schema.tables[tableName].fields.find(x => x.key === payload.field);
        let result = await fetch(schema.backend + '/achillea/nullifyTableField.php', {
            method: "PATCH",
            credentials: 'include',
            body: JSON.stringify({
                project: project,
                table: tableName,
                pk: payload.id,
                field: payload.field,
            })
        })
            .then(r => {
                if (r.status === 401) {
                    window.location.href = '/login.html'
                }
                return r
            })
            .then(r => r.json());

        if (result.status === 'error')
            return thunkAPI.rejectWithValue({
                payload: payload,
                table: tableName,
                page: state.tables.tableInfo[tableName].current_page,
                pkField: schema.tables[tableName].pk,
                result: result
            })

        return {
            payload: payload,
            table: tableName,
            page: state.tables.tableInfo[tableName].current_page,
            pkField: schema.tables[tableName].pk,
            result: result
        }
    },
)

export const deleteTableRecord = createAsyncThunk<{
    payload: {
        id: number | string
    },
    table: string,
    page: number,
    pkField: string,
    result: ApiResult<any>
}, {
    id: number | string
}, {
    state: RootState
}>(
    'table/deleteTableRecord',
    async (payload, thunkAPI) => {
        let state = thunkAPI.getState();

        let tableName = state.tables.activeTable;
        let project = state.project.name;

        const schema = schemas[project];

        const result = await apiFetch(schema.backend + '/achillea/deleteTableRecord.php', {
            method: "DELETE", body: JSON.stringify({
                project: project,
                table: tableName,
                pk: payload.id,
            })
        });

        if (!result.success)
            return thunkAPI.rejectWithValue({
                payload: payload,
                table: tableName,
                page: state.tables.tableInfo[tableName].current_page,
                pkField: schema.tables[tableName].pk,
                result: result
            })

        return {
            payload: payload,
            table: tableName,
            page: state.tables.tableInfo[tableName].current_page,
            pkField: schema.tables[tableName].pk,
            result: result
        }
    },
)


export const {
    setInitialTableState,
    setActiveTable,
    moveActiveTable,
    changePage,
    changePerPage,
    updateCurrentRecord,
    switchToTablePK,
    setTableFilters,
    addNewFilter,
    deleteFilter,
    deleteAllFilters,
    mergeTableFilters,
    selectFilterField,
    selectFilterOperation,
    setFilterValue,
    setOrderBy,
    changeOrderWay,
    setTableHidden,
    setSelectedTableFieldSet,
    setTableRefOpen,
    setTableRowOpen,
} = tablesSlice.actions

export default tablesSlice.reducer


function defaultFilterValue(type: FieldType) {

    switch (type) {
        case "number":
            return 0
        case "string":
        case "email":
        case "url":
        case "tel":
        case "image":
            return ''
        case "boolean":
            return true
        case "datetime":
        case "time":
        case "date":
            return datetimeDefValue(type)
        case "map":
            return undefined
        default:
            return undefined
    }
}
